import Constants from 'app/shared/constants';
import StringConstants from 'app/shared/constants/StringConstants';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';

// analytics
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import MultiAnalyticsModule from 'app/shared/MultiAnalyticsModule';

import pathsManifest from 'app/shared/pathsManifest';

import appLogger from 'app/shared/loggers/appLogger';

const ACCEPTED_HOST_APPS = ['ios', 'android'];

function getGA() {
    if (typeof window !== 'undefined' && window !== null && typeof window.ga !== 'undefined') {
        return window.ga;
    }

    return false;
}

function getMaInstance() {
    return MultiAnalyticsModule.getInstance();
}


const analyticsUtils = {
    getPageContextLabel(labelPrefix = '') {
        if (__CLIENT__) {
            const pathname = window.location.pathname;
            const pathList = pathsManifest.getAllPaths();

            let response = labelPrefix;

            const isLeadPath = pathname && pathname.indexOf(pathList.leadPage) > -1;
            const isPropertyDetailsListing = pathname && pathname.indexOf(pathList.propertyDetailsListing) > -1;

            if (isLeadPath) {
                response += 'Lead';
            }

            if (isPropertyDetailsListing) {
                response += 'Listing Post Activation';
            }

            return response;
        }
        return '';
    },

    formatCustomDimension(dimension) {
        if (dimension.indexOf('dimension') === -1) {
            return `dimension${dimension}`;
        }
        return dimension;
    },

    getCustomDimensionExperimentName(version = 'control', experimentName) {
        version = version.toLowerCase();
        const shortVersion = version === 'experiment' ? 'exp' : 'ctrl';
        return `${shortVersion}:${experimentName}`;
    },

    setLabsterExperimentDimension({ experiment, brand } = {}) {
        const {
            experimentName, customDimension, variation,
        } = experiment;

        const experimentNames = [analyticsUtils.getCustomDimensionExperimentName(variation, experimentName)];

        analyticsUtils.setCustomDimension(
            experimentNames.join('||'),
            analyticsUtils.formatCustomDimension(customDimension),
            brand
        );
    },

    track: {
        GAEventV2(gaEventObj, additionalParams = { action: '', label: '', value: '' }) {
            if (typeof additionalParams !== 'object') {
                appLogger.logError({
                    message: 'Please provide additional parameters to track with GA_EVENTS as an object.',
                    context: 'analyticsUtils.track.GAEventV2',
                });
                return null;
            }

            if (!gaEventObj) {
                appLogger.logError({
                    message: 'Error: No valid gaEvent found in AnalyticsConstants#GA_EVENTS',
                    context: 'analyticsUtils.track.GAEventV2',
                });
                return null;
            }

            return (dispatch, getState) => {
                if (getGA()) {
                    const { pathname } = getState().router.location;

                    const category = additionalParams.category || gaEventObj.category;
                    const action = additionalParams.action || gaEventObj.action;
                    const label = additionalParams.label || gaEventObj.label || pathname;
                    const value = additionalParams.value || gaEventObj.value;

                    // use as an adaptor for now
                    // TODO: remove track.GAEventV2 and update instances to use this.trackEvent
                    analyticsUtils.trackEvent(category, action, label, value);

                    // clear dynamic dimensions
                    analyticsUtils.clearCustomDimension(Constants.Z_CUSTOM_DIMENSION.EVENT_CONTENT);
                }
            };
        },
        optimizelyCTAEvent() {
            window.optimizely = window.optimizely || [];
            window.optimizely.push({
                type: 'event',
                eventName: 'ltleases_cta', // don't change this, the name is stuck due to Optimizely
                tags: {
                    revenue: 0, // Optional in cents as integer (500 == $5.00)
                    value: 0.00, // Optional as float
                },
            });
        },
    },

    clearCustomDimension(dimension) {
        if (!dimension) {
            appLogger.logError({
                message: 'ERROR @clearCustomDimension: dimension must be defined',
                context: 'analyticsUtils.clearCustomDimension',
            });
            return;
        }

        // TODO: enhance to handle multiple brands
        getMaInstance().getMa().setCustomDimensions({
            name: dimension,
            value: null,
        }, [getMaInstance().getTrackerIds().ua]);
    },

    setMobileAppCustomDimension() {
        if (__CLIENT__) {
            const searchParams = new URLSearchParams(window.location.search);
            const hostApp = searchParams.get('hostApp');

            analyticsUtils.setCustomDimension(
                ACCEPTED_HOST_APPS.includes(hostApp).toString(),
                AnalyticsConstants.GTM_CUSTOM_DIMENSION.IS_MOBILE_APP,
                StringConstants.GA_TRACKERS.gtm,
            );
        }
    },

    // trackerType here is going to be a brand or gtm
    setCustomDimension(value, dimension, trackerType) {
        if (!value) {
            appLogger.logError({
                message: 'ERROR @setCustomDimension: value must be defined',
                context: 'analyticsUtils.setCustomDimension',
            });
            return;
        }

        if (!dimension) {
            appLogger.logError({
                message: 'ERROR @setCustomDimension: dimension must be defined',
                context: 'analyticsUtils.setCustomDimension',
            });
            return;
        }

        if (!trackerType) {
            appLogger.logError({
                message: 'ERROR @setCustomDimension: trackerType must be defined',
                context: 'analyticsUtils.setCustomDimension',
            });
            return;
        }

        let trackerId = null;

        switch (trackerType) {
            case Constants.UNFORMATTED_BRAND_NAMES.zillow:
                trackerId = getMaInstance().getTrackerIds().ua;
                break;

            case StringConstants.GA_TRACKERS.gtm:
                trackerId = getMaInstance().getTrackerIds().gtm;
                break;

            case StringConstants.GA_TRACKERS.dsp:
                trackerId = getMaInstance().getTrackerIds().dsp;
                break;

            default:
                break;
        }

        getMaInstance().getMa().setCustomDimensions({
            name: dimension,
            value,
        }, [trackerId]);
    },

    // this corresponds to the comet_event in gtm
    trackEvent(category, action, label, value) {
        getMaInstance().getMa().trackEvent({
            category,
            action,
            label,
            value,
        });
    },

    // this corresponds to the send command in ua, and the comet_page_view_event in gtm
    trackPageView(path) {
        // ContentSquare tracking
        /* eslint-disable no-underscore-dangle */
        if (typeof window !== 'undefined' && window !== null) {
            window._uxa = window._uxa || [];
            window._uxa.push(['trackPageview', path]);
        }
        /* eslint-enable no-underscore-dangle */
        return (dispatch) => {
            if (!path) {
                appLogger.logError({
                    message: 'ERROR @trackPageView: path must be defined',
                    context: 'analyticsUtils.trackPageView',
                });
                return;
            }
            getMaInstance().getMa().trackPageView({
                path,
            }, null, () => dispatch(AnalyticsActions.comscore()));
        };
    },

    appendSuffix(textToAddSuffixTo, suffix, useDelimiter = false) {
        return `${textToAddSuffixTo}${useDelimiter ? DELIMITER : ''}${suffix}`;
    },
};

export default analyticsUtils;
